// Reusable theming mixin for vue-chartjs charts (Chart.js v2/v3 friendly)
// - UI-only: binds axis/legend/title/tooltip text and grid colors to CSS variables
// - Updates automatically when the global theme changes (window 'theme:changed')
// - Keeps existing data/options; merges theming into provided options

import { cssVar } from '@/utils/cssVars';

function themedColors() {
  return {
    text: cssVar('--text-primary', '#F8FAFC'),
    secondary: cssVar('--text-secondary', '#D1D5DB'),
    grid: cssVar('--grid-lines', 'rgba(255,255,255,0.12)'),
  };
}

function isV3(options) {
  // Chart.js v3 uses options.plugins.title, options.scales.{axis}.grid
  return options && (options.scales || options.plugins);
}

function mergeDeep(target, src) {
  // minimal deep merge to avoid bringing a lib; mutates target
  for (const k of Object.keys(src)) {
    const sv = src[k];
    const tv = target[k];
    if (sv && typeof sv === 'object' && !Array.isArray(sv)) {
      target[k] = tv && typeof tv === 'object' ? tv : {};
      mergeDeep(target[k], sv);
    } else {
      target[k] = sv;
    }
  }
  return target;
}

function themedOptions(base = {}) {
  const c = themedColors();
  const o = JSON.parse(JSON.stringify(base || {})); // simple clone

  // Try Chart.js v3 shape first
  if (isV3(o)) {
    o.scales = o.scales || {};
    Object.keys(o.scales).forEach((key) => {
      const s = o.scales[key] || {};
      s.ticks = Object.assign({}, s.ticks, { color: c.secondary });
      s.grid = Object.assign({}, s.grid, { color: c.grid });
      o.scales[key] = s;
    });

    o.plugins = o.plugins || {};
    o.plugins.legend = Object.assign({}, o.plugins.legend, { labels: Object.assign({}, o.plugins.legend && o.plugins.legend.labels, { color: c.secondary }) });
    o.plugins.title = Object.assign({}, o.plugins.title, { color: c.text });
    o.plugins.tooltip = Object.assign({}, o.plugins.tooltip, { titleColor: c.text, bodyColor: c.text, footerColor: c.text });
    return o;
  }

  // Chart.js v2 fallback
  // In v2, scales is an object with xAxes/yAxes arrays and gridLines/ticks
  o.scales = o.scales || {};
  const arrays = ['xAxes', 'yAxes'];
  arrays.forEach((ax) => {
    const arr = o.scales[ax] || [];
    o.scales[ax] = arr.map((axis) => {
      const a = Object.assign({}, axis);
      a.ticks = Object.assign({}, a.ticks, { fontColor: c.secondary });
      a.gridLines = Object.assign({}, a.gridLines, { color: c.grid, zeroLineColor: c.grid });
      return a;
    });
  });
  o.legend = Object.assign({}, o.legend, { labels: Object.assign({}, o.legend && o.legend.labels, { fontColor: c.secondary }) });
  o.title = Object.assign({}, o.title, { fontColor: c.text });
  o.tooltips = Object.assign({}, o.tooltips, { titleFontColor: c.text, bodyFontColor: c.text, footerFontColor: c.text });
  return o;
}

function applyThemeToChartInstance(vm) {
  // vm is a vue-chartjs component instance having this._chart (v2) or this.$data._chart
  try {
    const chart = vm._data?._chart || vm._chart || vm.chart; // best-effort
    if (!chart) return;

    const c = themedColors();
    if (chart.options) {
      // Try v3 pattern
      if (chart.options.scales) {
        Object.keys(chart.options.scales).forEach((key) => {
          const s = chart.options.scales[key] || {};
          if (s.ticks) s.ticks.color = c.secondary; // v3 (labels should use secondary)
          if (s.grid) s.grid.color = c.grid; // v3
          if (s.ticks && s.ticks.fontColor !== undefined) s.ticks.fontColor = c.secondary; // v2 fallback
          if (s.gridLines) {
            s.gridLines.color = c.grid;
            s.gridLines.zeroLineColor = c.grid;
          }
        });
      }

      // Plugins/legend/title/tooltips
      if (chart.options.plugins) {
        chart.options.plugins.legend = Object.assign({}, chart.options.plugins.legend, { labels: Object.assign({}, chart.options.plugins.legend && chart.options.plugins.legend.labels, { color: c.secondary }) });
        chart.options.plugins.title = Object.assign({}, chart.options.plugins.title, { color: c.text });
        chart.options.plugins.tooltip = Object.assign({}, chart.options.plugins.tooltip, { titleColor: c.text, bodyColor: c.text, footerColor: c.text });
      } else {
        chart.options.legend = Object.assign({}, chart.options.legend, { labels: Object.assign({}, chart.options.legend && chart.options.legend.labels, { fontColor: c.secondary }) });
        chart.options.title = Object.assign({}, chart.options.title, { fontColor: c.text });
        chart.options.tooltips = Object.assign({}, chart.options.tooltips, { titleFontColor: c.text, bodyFontColor: c.text, footerFontColor: c.text });
      }
    }
    chart.update && chart.update(0);
  } catch (e) {
    // no-op
  }
}

export default {
  methods: {
    themedOptions,
  },
  mounted() {
    // Render with themed options if renderChart is called here by wrapper.
    // For wrappers that call renderChart in mounted, we can't intercept that call,
    // so we update the chart instance after nextTick.
    this.$nextTick(() => applyThemeToChartInstance(this));
    this.__onThemeChanged = () => applyThemeToChartInstance(this);
    window.addEventListener('theme:changed', this.__onThemeChanged);
  },
  beforeDestroy() {
    if (this.__onThemeChanged) window.removeEventListener('theme:changed', this.__onThemeChanged);
  }
};
